import { useAuth } from '../context/AuthContext';
import {
  canAccess,
  canCreate,
  canEdit,
  canDelete,
  canApprove,
  isAdmin as checkIsAdmin,
  getRolePermissions,
  getAccessibleResources,
} from '../utils/rolePermissions';

/**
 * Custom hook for checking user permissions
 * 
 * Usage example:
 * const { canAccess, canDelete, isAdmin } = usePermissions();
 * 
 * if (canAccess(RESOURCES.DASHBOARD)) {
 *   // Show dashboard content
 * }
 * 
 * if (canDelete(RESOURCES.SALES, record.status)) {
 *   // Show delete button
 * }
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const userRole = user?.role;

  return {
    // Check if user can access a resource
    canAccess: (resource) => canAccess(userRole, resource),
    
    // Check if user can create in a resource
    canCreate: (resource) => canCreate(userRole, resource),
    
    // Check if user can edit a resource
    canEdit: (resource) => canEdit(userRole, resource),
    
    // Check if user can delete from a resource
    canDelete: (resource, status = null) => canDelete(userRole, resource, status),
    
    // Check if user can approve items
    canApprove: (resource) => canApprove(userRole, resource),
    
    // Check if user is admin
    isAdmin: () => checkIsAdmin(userRole),
    
    // Get all permissions for current user
    permissions: getRolePermissions(userRole),
    
    // Get list of resources user can access
    accessibleResources: getAccessibleResources(userRole),
    
    // Get current user role
    role: userRole,
  };
};

export default usePermissions;

